import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { sessionAPI, applicationAPI } from '../../services/api';
import { FaCalendarAlt, FaArrowLeft, FaSave, FaUsers, FaFileAlt, FaEdit, FaTrash } from 'react-icons/fa';

const SessionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        application_deadline: '',
        status: 'upcoming',
        max_applicants: null
    });

    useEffect(() => {
        loadSessionData();
    }, [id]);

    const loadSessionData = async () => {
        try {
            setLoading(true);
            const [sessionRes, appsRes] = await Promise.all([
                sessionAPI.getById(id),
                applicationAPI.getBySession(id)
            ]);

            const sessionData = sessionRes.session || sessionRes;
            setSession(sessionData);
            setApplications(appsRes.applications || []);
            
            // Set form data for editing
            if (sessionData) {
                setFormData({
                    name: sessionData.name || '',
                    description: sessionData.description || '',
                    start_date: sessionData.start_date ? sessionData.start_date.split('T')[0] : '',
                    end_date: sessionData.end_date ? sessionData.end_date.split('T')[0] : '',
                    application_deadline: sessionData.application_deadline ? sessionData.application_deadline.split('T')[0] : '',
                    status: sessionData.status || 'upcoming',
                    max_applicants: sessionData.max_applicants || null
                });
            }
        } catch (err) {
            console.error('Failed to load session:', err);
            toast.error('Failed to load session details.');
            navigate('/admin/sessions');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSave = async () => {
        try {
            await sessionAPI.update(id, formData);
            toast.success('Session updated successfully!');
            setEditing(false);
            loadSessionData();
        } catch (err) {
            console.error('Failed to update session:', err);
            toast.error('Failed to update session. Please try again.');
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirmAction('Are you sure you want to delete this session? This action cannot be undone.');
        if (!confirmed) return;

        try {
            await sessionAPI.delete(id);
            toast.success('Session deleted successfully!');
            navigate('/admin/sessions');
        } catch (err) {
            console.error('Failed to delete session:', err);
            toast.error('Failed to delete session. Please try again.');
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'active': { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
            'upcoming': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Upcoming' },
            'closed': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' }
        };
        const config = statusConfig[status] || statusConfig['closed'];
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!session) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Session Not Found</h2>
                    <Link to="/admin/sessions" className="text-blue-600 hover:text-blue-700">
                        ← Back to Sessions
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=sessions" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                                <FaCalendarAlt className="text-primary-600" />
                                {editing ? 'Edit Session' : session.name}
                            </h1>
                            {!editing && getStatusBadge(session.status)}
                        </div>
                        <div className="flex gap-2">
                            {!editing ? (
                                <>
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <FaEdit /> Edit
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                                    >
                                        <FaTrash /> Delete
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={handleSave}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                    >
                                        <FaSave /> Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditing(false);
                                            loadSessionData();
                                        }}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Session Details Form */}
                    {editing ? (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Session Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="end_date"
                                        value={formData.end_date}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Application Deadline
                                    </label>
                                    <input
                                        type="date"
                                        name="application_deadline"
                                        value={formData.application_deadline}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="upcoming">Upcoming</option>
                                        <option value="active">Active</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Max Applicants (optional)
                                    </label>
                                    <input
                                        type="number"
                                        name="max_applicants"
                                        value={formData.max_applicants || ''}
                                        onChange={handleChange}
                                        min="1"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {session.description && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <p className="text-gray-700">{session.description}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <p className="text-gray-700">
                                        {session.start_date ? new Date(session.start_date).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    <p className="text-gray-700">
                                        {session.end_date ? new Date(session.end_date).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
                                    <p className="text-gray-700">
                                        {session.application_deadline ? new Date(session.application_deadline).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                            </div>

                            {session.max_applicants && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Applicants</label>
                                    <p className="text-gray-700">{session.max_applicants}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Applications List */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <FaFileAlt className="text-primary-600" />
                            Applications ({applications.length})
                        </h2>
                    </div>

                    {applications.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                            <p>No applications for this session yet</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {applications.map((app) => (
                                <div key={app.id} className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-semibold text-lg text-gray-900">
                                                    {app.first_name || 'N/A'} {app.surname || ''}
                                                </h3>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                    app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                    app.status === 'under_review' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {app.status?.replace('_', ' ') || 'submitted'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600">{app.email}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Submitted: {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <Link
                                            to={`/admin/applications/${app.id}`}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            View Details
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SessionDetail;

